/**
 * src/lib/voice/sttProvider.js — Pluggable STT provider interface (Phase 90-01)
 *
 * Single entry point for Speech-to-Text across client and server. Swap providers
 * via `localStorage.acn_stt_provider` (client) or `process.env.STT_PROVIDER`
 * (server). Default: 'whisper'. Adding a provider means registering it here and
 * implementing `{ name, transcribe(audioBuffer, opts) }` — no caller changes.
 *
 * Providers:
 *   - 'whisper'          OpenAI Whisper (default). Best accuracy for marine vocab.
 *   - 'gemini'           Google Gemini 2.5 Flash. Multimodal path, free tier.
 *   - 'deepgram'         Stub — Phase 90-02 will implement streaming partials.
 *   - 'openai-realtime'  Stub — Phase 90-02 alternative streaming option.
 *
 * Transcribe contract:
 *   transcribe(audioBuffer, {
 *     mimeType,     // e.g. 'audio/webm;codecs=opus'
 *     language,     // ISO code like 'en' | 'es' | 'auto'
 *     vocabHints,   // [{ term, misheard_as?, language? }] — provider renders as needed
 *     fileName,     // upload filename hint (Whisper)
 *     signal,       // AbortSignal for caller-driven timeouts
 *   }) => Promise<{ text, engine, rawText? }>
 *
 * Callers never branch on provider name. Vocab hints are passed as structured
 * data; each provider decides how to inject them (Whisper prompt, Gemini
 * systemInstruction, Deepgram keyterm param, etc.).
 */

export const STT_PROVIDER_NAMES = ['whisper', 'gemini', 'deepgram', 'openai-realtime'];

const LS_KEY = 'acn_stt_provider';
const LS_LEGACY_KEY = 'acn_stt_engine';

let legacyMigrationLogged = false;

/**
 * Resolve the default provider name from environment.
 * Client: localStorage.acn_stt_provider → legacy acn_stt_engine → 'whisper'
 * Server: process.env.STT_PROVIDER → 'whisper'
 */
export function resolveDefaultProvider() {
  // Server env wins when set — per plan, STT_PROVIDER overrides client preference.
  if (typeof process !== 'undefined' && process.env && process.env.STT_PROVIDER) {
    const env = process.env.STT_PROVIDER;
    if (STT_PROVIDER_NAMES.includes(env)) return env;
  }
  // Client preference via localStorage (browser or JSDOM test).
  if (typeof localStorage !== 'undefined') {
    try {
      const v = localStorage.getItem(LS_KEY);
      if (v && STT_PROVIDER_NAMES.includes(v)) return v;
      const legacy = localStorage.getItem(LS_LEGACY_KEY);
      if (legacy && STT_PROVIDER_NAMES.includes(legacy)) {
        localStorage.setItem(LS_KEY, legacy);
        if (!legacyMigrationLogged) {
          console.log(`[sttProvider] migrated ${LS_LEGACY_KEY}='${legacy}' → ${LS_KEY}`);
          legacyMigrationLogged = true;
        }
        return legacy;
      }
    } catch { /* localStorage unavailable */ }
  }
  return 'whisper';
}

/**
 * Get a provider instance by name. Default resolves via environment.
 * @param {string} [name]  Explicit provider name; omit to use default.
 * @returns {{ name: string, transcribe: (audio: Buffer|Uint8Array, opts: object) => Promise<{text: string, engine: string, rawText?: string}> }}
 */
export function getSttProvider(name) {
  const resolved = name || resolveDefaultProvider();
  switch (resolved) {
    case 'whisper': return whisperProvider;
    case 'gemini': return geminiProvider;
    case 'deepgram': return stubProvider('deepgram');
    case 'openai-realtime': return stubProvider('openai-realtime');
    default:
      throw new Error(`Unknown STT provider: ${resolved}. Supported: ${STT_PROVIDER_NAMES.join(', ')}`);
  }
}

// ── Provider implementations ────────────────────────────────────────────

// NOTE: transcribe() is server-only. Client uses { name } to send a preference.
// Dynamic imports carry @vite-ignore so the client bundle does not pull in
// server-only deps (undici, chroma-client, etc.). On the client, transcribe()
// fails with a clear error — clients should POST to /api/ai.

const whisperProvider = {
  name: 'whisper',
  async transcribe(audioBuffer, opts = {}) {
    const mod = await import(/* @vite-ignore */ '../../../lib/utils/stt.js');
    return mod.transcribeWithWhisperRaw(audioBuffer, {
      mimeType: opts.mimeType,
      language: opts.language,
      prompt: vocabHintsToWhisperPrompt(opts.vocabHints, opts.prompt),
      fileName: opts.fileName,
      signal: opts.signal,
    });
  },
};

const geminiProvider = {
  name: 'gemini',
  async transcribe(audioBuffer, opts = {}) {
    const mod = await import(/* @vite-ignore */ '../../../lib/utils/stt.js');
    return mod.transcribeWithGeminiRaw(audioBuffer, {
      mimeType: opts.mimeType,
      systemInstruction: vocabHintsToGeminiInstruction(opts.vocabHints, opts.systemInstruction),
      signal: opts.signal,
    });
  },
};

function stubProvider(name) {
  return {
    name,
    async transcribe() {
      const detail = name === 'deepgram'
        ? 'Deepgram Nova-3 streaming integration is planned for Phase 90-02. '
        + 'Will route through a signed-token endpoint so DEEPGRAM_API_KEY never reaches the client.'
        : 'OpenAI Realtime STT integration is planned for Phase 90-02.';
      throw new Error(`STT provider "${name}" not implemented — see Phase 90-02. ${detail}`);
    },
  };
}

// ── Vocab hint rendering ────────────────────────────────────────────────

/**
 * Turn vocab hints into a Whisper `prompt` string (comma-joined terms).
 * Existing prompt string (if caller passed one) takes precedence.
 */
export function vocabHintsToWhisperPrompt(hints, existingPrompt) {
  if (existingPrompt) return existingPrompt;
  if (!Array.isArray(hints) || hints.length === 0) return null;
  const terms = hints
    .slice(0, 15)
    .map(h => (typeof h === 'string' ? h : h.term))
    .filter(Boolean);
  return terms.length ? terms.join(', ') : null;
}

/**
 * Turn vocab hints into a Gemini systemInstruction string.
 * Existing systemInstruction (if caller passed one) takes precedence.
 */
export function vocabHintsToGeminiInstruction(hints, existingInstruction) {
  if (existingInstruction) return existingInstruction;
  if (!Array.isArray(hints) || hints.length === 0) return null;
  const terms = hints
    .slice(0, 15)
    .map(h => (typeof h === 'string' ? h : h.term))
    .filter(Boolean);
  if (!terms.length) return null;
  return `You are transcribing speech that may contain these domain terms — preserve exact spelling and capitalization when you hear them: ${terms.join(', ')}.`;
}
